interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showPageNumbers?: number;
}

export function Pagination({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  showPageNumbers = 5 
}: PaginationProps) {
  if (totalPages <= 1) {
    return null;
  }

  const getPageNumbers = () => {
    const pages = [];
    const halfShow = Math.floor(showPageNumbers / 2);
    
    let startPage = Math.max(0, currentPage - halfShow);
    const endPage = Math.min(totalPages - 1, startPage + showPageNumbers - 1);
    
    // Adjust startPage if we're near the end
    if (endPage - startPage + 1 < showPageNumbers) {
      startPage = Math.max(0, endPage - showPageNumbers + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();

  const baseBtn: React.CSSProperties = {
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: 'transparent',
  };

  const activeBtn: React.CSSProperties = {
    ...baseBtn,
    backgroundColor: 'var(--color-info)',
    color: 'var(--color-on-brand)',
  };

  const inactiveBtn: React.CSSProperties = {
    ...baseBtn,
    color: 'var(--color-text-secondary)',
  };

  const disabledBtn: React.CSSProperties = {
    ...baseBtn,
    color: 'var(--color-text-disabled)',
    cursor: 'not-allowed',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 0}
        style={currentPage === 0 ? disabledBtn : inactiveBtn}
      >
        Previous
      </button>

      {pageNumbers[0] > 0 && (
        <>
          <button onClick={() => onPageChange(0)} style={inactiveBtn}>1</button>
          {pageNumbers[0] > 1 && (
            <span style={{ padding: '8px', color: 'var(--color-text-disabled)' }}>...</span>
          )}
        </>
      )}

      {pageNumbers.map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          style={page === currentPage ? activeBtn : inactiveBtn}
        >
          {page + 1}
        </button>
      ))}

      {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
        <>
          {pageNumbers[pageNumbers.length - 1] < totalPages - 2 && (
            <span style={{ padding: '8px', color: 'var(--color-text-disabled)' }}>...</span>
          )}
          <button onClick={() => onPageChange(totalPages - 1)} style={inactiveBtn}>
            {totalPages}
          </button>
        </>
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages - 1}
        style={currentPage === totalPages - 1 ? disabledBtn : inactiveBtn}
      >
        Next
      </button>
    </div>
  );
}
